import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container,
    Grid,
    Snackbar,
    Alert,
    Card,
    CardContent,
    Skeleton,
    Box
} from '@mui/material';

import DeviceCard from '../components/devices/DeviceCard';
import PageTitle from "../components/layout/PageTitle";
import { getDevicesByHouse } from "../api_calls/devices";
import HouseMonitoringWelcomeMsg from '../components/houses/house reports/HouseMonitoringWelcomeMsg';
import { useHouseDetails } from '../context/HouseDetailsContext';

const Devices = () => {
    const { id } = useParams();
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { houseName, houseAddress } = useHouseDetails();

    // Refresh devices when house changes
    const refreshDevices = useCallback(() => {
        setLoading(true);
        getDevicesByHouse(id)
            .then(response => {
                setDevices(response.data || []);
                setLoading(false);
            })
            .catch(error => {
                console.error(error);
                setLoading(false);
            });
    }, [id]);

    useEffect(() => {
        refreshDevices();
    }, [refreshDevices]);

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Skeleton component matching DeviceCard size
    const DeviceCardSkeleton = () => (
        <Card
            sx={{
                borderRadius: '25px',
                padding: 2,
                height: 350,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 'none',
            }}
        >
            <CardContent
                sx={{
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Skeleton variant="circular" width={36} height={36} />
                        <Box>
                            <Skeleton variant="text" width={120} height={24} />
                            <Skeleton variant="text" width={60} height={16} />
                        </Box>
                    </Box>
                    <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: '12px' }} />
                </Box>
                <Box sx={{ flex: 1, mt: 2 }}>
                    <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2 }} />
                </Box>
            </CardContent>
        </Card>
    );

    return (
        <>
            <PageTitle title="Devices" />
            {/* Welcome message and house details section */}
            {houseName && houseAddress && (
                <Container sx={{ my: 5 }}>
                    <HouseMonitoringWelcomeMsg
                        id={id}
                        houseAddress={houseAddress}
                        houseName={houseName}
                        showCost={false}
                    />
                </Container>
            )}
            <Container sx={{ my: 5 }}>
                {loading ? (
                    <Grid container spacing={3}>
                        {[1, 2].map((index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <DeviceCardSkeleton />
                            </Grid>
                        ))}
                    </Grid>
                ) : devices.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        No devices exist for now. Add devices to get started.
                    </Alert>
                ) : (
                    <Grid container spacing={3}>
                        {devices.map(device => (
                            <Grid item xs={12} sm={6} md={4} key={device.id}>
                                <DeviceCard
                                    device={device}
                                    refreshDevices={refreshDevices}
                                    setSnackbar={setSnackbar}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Container>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    variant="filled"
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Devices;
